/**
 * Human Presence avatar (§0.9): one combo key → one animal on one scene,
 * two stacked images clipped to the same soft disc the symbol pool used.
 * The pool and the key format are the product contract and live in
 * src/shared/ember-avatars.js; any value that is not a pool combo (a retired
 * lucide symbol key, an unknown string) normalizes onto a deterministic
 * combo, so a stale row can never render an empty circle — and always
 * renders the same picture.
 *
 * Deliberately non-photoreal: gentle animals in quiet places, painted in the
 * image house style. A creature someone chooses to be, not a portrait of who
 * they are.
 */
import {
  normalizeAvatarKey,
  avatarAnimalSrc,
  avatarSceneSrc,
  avatarRenditionWidth,
} from '../../../../../shared/ember-avatars.js';
import './PresenceAvatar.css';

function tintIndex(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % 6;
}

export function PresenceAvatar({ avatarKey, size = 32 }: { avatarKey: string; size?: number }) {
  const { animal, scene } = normalizeAvatarKey(avatarKey);
  // 2x of the painted size, snapped to the shared rendition ladder; the 1x
  // candidate rides along for low-DPR screens (§2 image delivery contract).
  const w1 = avatarRenditionWidth(size);
  const w2 = avatarRenditionWidth(size * 2);
  return (
    <span
      className={`presence-avatar presence-avatar-tint-${tintIndex(`${animal}:${scene}`)}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <img
        className="presence-avatar-scene"
        src={avatarSceneSrc(scene, w2)}
        srcSet={`${avatarSceneSrc(scene, w1)} 1x, ${avatarSceneSrc(scene, w2)} 2x`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        draggable={false}
      />
      <img
        className="presence-avatar-animal"
        src={avatarAnimalSrc(animal, w2)}
        srcSet={`${avatarAnimalSrc(animal, w1)} 1x, ${avatarAnimalSrc(animal, w2)} 2x`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        draggable={false}
      />
    </span>
  );
}
